import * as XLSX from 'xlsx'

/** Excel hücresine yazılabilecek ham değer tipleri. */
export type ExcelCellValue = string | number | boolean | null | undefined

export interface ExcelExportColumn {
  header: string
  /**
   * Excel sayı biçim kodu (ör. `'#,##0.000'`). Sayısal kolonlarda **zorunlu**:
   * hücreye ham JS `number` yazılır, virgül/nokta ayırıcıyı biz metne gömmeyiz —
   * Excel bu kodu açan kişinin bölge ayarına göre gösterir (tr-TR'de ondalık
   * virgül, binlik nokta). Metin kolonlarında (`undefined`) hücre olduğu gibi kalır.
   */
  numberFormat?: string
}

export interface ExportRowsToExcelParams {
  /** Uzantısız veya `.xlsx` ile bitebilir; sabit bir kimlik değil, kullanıcıya inen dosya adı. */
  filename: string
  sheetName?: string
  columns: ExcelExportColumn[]
  rows: ExcelCellValue[][]
}

const DEFAULT_SHEET_NAME = 'Sayfa1'

/**
 * Tüm tablo/pivot ekranlarının tek, standart Excel çıkış noktası.
 * Kolon genişlikleri başlık/hücre uzunluğuna göre otomatik ayarlanır.
 */
export function exportRowsToExcel({
  filename,
  sheetName = DEFAULT_SHEET_NAME,
  columns,
  rows,
}: ExportRowsToExcelParams): void {
  const headers = columns.map((column) => column.header)
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows])

  // `aoa_to_sheet` sayı hücrelerini zaten `t: 'n'` olarak işaretler (metne çevirmez);
  // burada sadece görüntü biçimini uyguluyoruz.
  columns.forEach((column, columnIndex) => {
    if (!column.numberFormat) return
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const address = XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex })
      const cell = worksheet[address]
      if (cell && cell.t === 'n') cell.z = column.numberFormat
    }
  })

  worksheet['!cols'] = columns.map((column, columnIndex) => {
    const longest = rows.reduce((max, row) => {
      const cell = row[columnIndex]
      const length = cell === null || cell === undefined ? 0 : String(cell).length
      return Math.max(max, length)
    }, column.header.length)
    return { wch: Math.min(Math.max(longest + 2, 8), 60) }
  })

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31))

  const safeName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`
  XLSX.writeFile(workbook, safeName)
}

/** Dosya adı için `YYYY-MM-DD` bugünün tarihi (tesis yerel takvim günü değil, UI anlıklığı yeterli). */
export function exportTimestamp(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}
